// src/services/api.js
import axios from 'axios';

const getApiKey = () => {
  // 1. Из глобальной переменной (env.js)
  if (typeof window !== 'undefined' && window.env && window.env.VITE_API_KEY) {
    console.log('🔑 Using API Key from window.env:', window.env.VITE_API_KEY);
    return window.env.VITE_API_KEY;
  }
  
  // 2. Из import.meta.env (Vite development)
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_KEY) {
    console.log('🔑 Using API Key from import.meta.env:', import.meta.env.VITE_API_KEY);
    return import.meta.env.VITE_API_KEY;
  }
  
  // 3. Fallback - используем web_site ключ
  console.warn('⚠️ API Key not found in environment, using default');
  return 'web_site';
};

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location.hostname.includes('localhost')) {
    return 'http://localhost:8080/api/v1';
  }
  return 'https://taskit.hsbk.ru/api/v1';
};

const API_BASE_URL = getApiBaseUrl();
const API_KEY = getApiKey();

console.log('🔧 API Configuration:', {
  baseURL: API_BASE_URL,
  hasApiKey: !!API_KEY,
  apiKeyLength: API_KEY ? API_KEY.length : 0,
  environment: typeof window !== 'undefined' ? window.location.hostname : 'server'
});

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Интерцептор для добавления API Key и токена к запросам
api.interceptors.request.use(
  (config) => {
    // Всегда добавляем API Key
    if (API_KEY) {
      config.headers['X-API-Key'] = API_KEY;
      console.log('📤 Adding API Key to request:', config.url);
    } else {
      console.error('❌ API Key is missing! Requests will fail.');
    }
    
    // Добавляем токен авторизации если есть
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    console.log('🚀 Request headers:', {
      url: config.url,
      hasApiKey: !!config.headers['X-API-Key'],
      apiKeyLength: config.headers['X-API-Key'] ? config.headers['X-API-Key'].length : 0,
      hasToken: !!token
    });
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Интерцептор для обработки ошибок
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('❌ Request failed:', {
      url: error.config?.url,
      status: error.response?.status,
      data: error.response?.data,
    });

    // ✅ СПЕЦИАЛЬНАЯ ОБРАБОТКА ДЛЯ НЕАКТИВНОГО АККАУНТА
    if (error.response?.status === 403 && 
        error.response?.data?.code === 'inactive_account') {
      
      console.log('🔴 Inactive account detected');
      
      // Очищаем все данные
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      
      // Сохраняем информацию о причине
      localStorage.setItem('inactive_reason', JSON.stringify({
        message: error.response.data.message || 'Ваш аккаунт деактивирован',
        timestamp: new Date().toISOString()
      }));
      
      // Редирект на страницу неактивного аккаунта
      window.location.href = '/_admin/inactive';
      
      return Promise.reject(error);
    }
    
    // Обработка 401 (Unauthorized) - токен истек
    if (error.response?.status === 401) {
      console.log('🔐 Unauthorized, redirecting to login');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/_admin/login';
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);


export const authAPI = {
  // OAuth2 login (form-data)
  login: async (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);
    
    const response = await api.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // JSON login
  loginJson: async (email, password) => {
    const response = await api.post('/auth/login-json', {
      email,
      password,
    });
    return response.data;
  },

  // Register
  register: async (name, email, password) => {
    const response = await api.post('/auth/register', {
      name,
      email,
      password,
    });
    return response.data;
  },

  // Get current user
  getCurrentUser: async () => {
    const response = await api.get('/users/me');
    return response.data;
  },

  // Telegram auth - получаем ссылку для авторизации
  getTelegramAuthUrl: async () => {
    const response = await api.get('/auth/telegram/auth-url');
    return response.data;
  },

  // Telegram auth - обработка callback
  telegramCallback: async (telegramData) => {
    const response = await api.post('/auth/telegram/callback', telegramData);
    return response.data;
  },

  // Verify Telegram auth
  verifyTelegramAuth: async (telegramData) => {
    console.log('📨 Sending Widget Telegram data:', telegramData);
    
    const requestData = {
      id: parseInt(telegramData.id),
      first_name: telegramData.first_name,
      last_name: telegramData.last_name || '',
      username: telegramData.username || '',
      photo_url: telegramData.photo_url || '',
      auth_date: parseInt(telegramData.auth_date),
      hash: telegramData.hash
    };
    
    console.log('📤 Final request data:', requestData);
    
    const response = await api.post('/auth/telegram/verify', requestData);
    return response.data;
  },
};

// export const usersAPI = {
//   // Получить список пользователей
//   getUsers: async (params = {}) => {
//     const response = await api.get('/users/', { params });
//     return response.data;
//   },

//   // Получить пользователя по ID
//   getUser: async (userId) => {
//     const response = await api.get(`/users/${userId}`);
//     return response.data;
//   },

//   // Обновить пользователя
//   updateUser: async (userId, userData) => {
//     const response = await api.put(`/users/${userId}`, userData);
//     return response.data;
//   },

//   // Удалить пользователя
//   deleteUser: async (userId) => {
//     const response = await api.delete(`/users/${userId}`);
//     return response.data;
//   },

//   // Создать пользователя
//   createUser: async (userData) => {
//     const response = await api.post('/users/', userData);
//     return response.data;
//   },
// };

export const usersAPI = {
  // Получить список пользователей
  getUsers: async (params = {}) => {
    const response = await api.get('/users/', { params });
    return response.data;
  },

  // Получить пользователя по ID
  getUser: async (userId) => {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },

  // Обновить пользователя
  updateUser: async (userId, userData) => {
    const response = await api.put(`/users/${userId}`, userData);
    return response.data;
  },

  // Удалить пользователя
  deleteUser: async (userId) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },

  // Создать пользователя
  createUser: async (userData) => {
    const response = await api.post('/users/', userData);
    return response.data;
  },

  // Пакетная загрузка пользователей по ID
  getUsersBatch: async (userIds) => {
    if (!userIds || userIds.length === 0) return [];
    
    // Если API поддерживает фильтрацию по нескольким ID
    const response = await api.get('/users/', {
      params: { user_ids: userIds.join(',') }
    });
    return response.data;
  }
};

export const notificationsAPI = {
  // Получить уведомления пользователя
  getNotifications: async (params = {}) => {
    const response = await api.get('/notifications/', { params });
    return response.data;
  },

  // Получить статистику уведомлений
  getStats: async () => {
    const response = await api.get('/notifications/stats');
    return response.data;
  },

  // Отметить уведомление как прочитанное
  markAsRead: async (notificationId) => {
    const response = await api.patch(`/notifications/${notificationId}/read`);
    return response.data;
  },

  // Отметить все уведомления как прочитанные
  markAllAsRead: async () => {
    const response = await api.post('/notifications/read-all');
    return response.data;
  },

  // Отправить уведомление (универсальный метод)
  sendUniversalNotification: async (notificationData) => {
    const response = await api.post('/notifications/send', notificationData);
    return response.data;
  },

  

  // Быстрая отправка уведомления
  quickNotify: async (user_id, title, message, send_telegram = true, send_websocket = true) => {
    const response = await api.post('/notifications/quick-notify', null, {
      params: {
        user_id,
        title,
        message,
        send_telegram,
        send_websocket
      }
    });
    return response.data;
  },

  // Массовая рассылка
  broadcast: async (broadcastData) => {
    const response = await api.post('/notifications/broadcast', broadcastData);
    return response.data;
  },

  // Рассылка администраторам
  broadcastToAdmins: async (title, message, send_telegram = true, send_websocket = true, priority = 'normal') => {
    const response = await api.post('/notifications/broadcast/admins', null, {
      params: {
        title,
        message,
        send_telegram,
        send_websocket,
        priority
      }
    });
    return response.data;
  },

  // Рассылка модераторам
  broadcastToModerators: async (title, message, send_telegram = true, send_websocket = true, priority = 'normal') => {
    const response = await api.post('/notifications/broadcast/moderators', null, {
      params: {
        title,
        message,
        send_telegram,
        send_websocket,
        priority
      }
    });
    return response.data;
  },

  // Рассылка всем пользователям
  broadcastToAll: async (title, message, send_telegram = true, send_websocket = true, priority = 'normal') => {
    const response = await api.post('/notifications/broadcast/all', null, {
      params: {
        title,
        message,
        send_telegram,
        send_websocket,
        priority
      }
    });
    return response.data;
  },

  // Получить статистику для рассылок
  getBroadcastStats: async () => {
    const response = await api.get('/notifications/broadcast/stats');
    return response.data;
  },

  // Тестовый endpoint
  testSend: async (user_id = 1, title = "Тестовое уведомление", message = "Это тестовое сообщение") => {
    const response = await api.post('/notifications/test-send', null, {
      params: {
        user_id,
        title,
        message
      }
    });
    return response.data;
  },

  // Тест WebSocket
  testWebSocket: async (user_id = 1, title = "Тест WebSocket", message = "Это тестовое уведомление через WebSocket") => {
    const response = await api.post('/notifications/test-websocket', null, {
      params: {
        user_id,
        title,
        message
      }
    });
    return response.data;
  },

  // Получить статус WebSocket соединений
  getWebSocketStatus: async () => {
    const response = await api.get('/notifications/websocket/status');
    return response.data;
  },

  // Очистка WebSocket соединений
  cleanupWebSocket: async () => {
    const response = await api.post('/notifications/websocket/cleanup');
    return response.data;
  }
};

// export const tasksAPI = {
//   // Получить список задач
//   getTasks: async (params = {}) => {
//     const response = await api.get('/tasks/', { params });
//     return response.data;
//   },

//   // Получить задачу по ID
//   getTask: async (taskId) => {
//     const response = await api.get(`/tasks/${taskId}`);
//     return response.data;
//   },

//   // Создать задачу
//   createTask: async (taskData) => {
//     const response = await api.post('/tasks/', taskData);
//     return response.data;
//   },

//   // Обновить задачу
//   updateTask: async (taskId, taskData) => {
//     const response = await api.put(`/tasks/${taskId}`, taskData);
//     return response.data;
//   },

//   // Удалить задачу
//   deleteTask: async (taskId) => {
//     await api.delete(`/tasks/${taskId}`);
//     return { success: true };
//   },

//   // Получить статистику задач
//   getStats: async () => {
//     const response = await api.get('/tasks/stats');
//     return response.data;
//   },

//   // Завершить задачу
//   completeTask: async (taskId, actualHours = null) => {
//     const response = await api.post(`/tasks/${taskId}/complete`, null, {
//       params: { actual_hours: actualHours }
//     });
//     return response.data;
//   },

//   // Назначить задачу
//   assignTask: async (taskId, assigneeId) => {
//     const response = await api.post(`/tasks/${taskId}/assign/${assigneeId}`);
//     return response.data;
//   },

//   // Добавить комментарий
//   addComment: async (taskId, commentData) => {
//     const response = await api.post(`/tasks/${taskId}/comments`, commentData);
//     return response.data;
//   },

//   // Получить комментарии задачи
//   getTaskComments: (taskId) => {
//     return apiClient.get(`/tasks/${taskId}/comments`);
//   },

//   // Добавить комментарий к задаче
//   // addComment: (taskId, text) => {
//   //   return apiClient.post(`/tasks/${taskId}/comments`, { text });
//   // },

//   // Удалить комментарий
//   deleteComment: (taskId, commentId) => {
//     return apiClient.delete(`/tasks/${taskId}/comments/${commentId}`);
//   },

//   // Добавить вложение
//   addAttachment: async (taskId, file) => {
//     const formData = new FormData();
//     formData.append('file', file);
    
//     const response = await api.post(`/tasks/${taskId}/attachments`, formData, {
//       headers: {
//         'Content-Type': 'multipart/form-data',
//       },
//     });
//     return response.data;
//   },

//   // Скачать вложение
//   downloadAttachment: async (taskId, attachmentId) => {
//     const response = await api.get(`/tasks/${taskId}/attachments/${attachmentId}`, {
//       responseType: 'blob'
//     });
//     return response.data;
//   }
// };
// src/services/api.js

// ... остальной код без изменений ...

// src/services/api.js

// ... остальной код остается без изменений ...

export const tasksAPI = {
  // Получить список задач
  getTasks: async (params = {}) => {
    const response = await api.get('/tasks/', { params });
    return response.data;
  },

  // Получить задачу по ID
  getTask: async (taskId) => {
    const response = await api.get(`/tasks/${taskId}`);
    return response.data;
  },

  // Создать задачу
  createTask: async (taskData) => {
    console.log('📤 Sending task creation request:', taskData);
    const response = await api.post('/tasks/', taskData);
    console.log('✅ Task creation response:', response.data);
    
    // API возвращает объект с ID созданной задачи
    return response.data;
  },

  // Обновить задачу
  updateTask: async (taskId, taskData) => {
    const response = await api.put(`/tasks/${taskId}`, taskData);
    return response.data;
  },

  // Удалить задачу
  deleteTask: async (taskId) => {
    await api.delete(`/tasks/${taskId}`);
    return { success: true };
  },

  // Получить статистику задач
  getStats: async () => {
    const response = await api.get('/tasks/stats');
    return response.data;
  },

  // Завершить задачу
  completeTask: async (taskId, actualHours = null) => {
    const response = await api.post(`/tasks/${taskId}/complete`, null, {
      params: { actual_hours: actualHours }
    });
    return response.data;
  },

  // Назначить задачу
  assignTask: async (taskId, assigneeId) => {
    const response = await api.post(`/tasks/${taskId}/assign/${assigneeId}`);
    return response.data;
  },

  // Получить комментарии задачи - УБИРАЕМ, т.к. они приходят с задачей
  getComments: async (taskId) => {
    console.log('⚠️ Comments endpoint not available, returning empty array');
    return [];
  },

  // Добавить комментарий - УБИРАЕМ, т.к. endpoint не работает
  // addComment: async (taskId, text) => {
  //   console.log('⚠️ Add comment endpoint not available');
  //   throw new Error('Добавление комментариев временно недоступно');
  // },
  addComment: async (taskId, content) => {
    console.log('📤 Adding comment to task:', taskId, content);
    const response = await api.post(`/tasks/${taskId}/comments`, {
      content: content
    });
    console.log('✅ Comment added:', response.data);
    return response.data;
  },

  // Удалить комментарий - УБИРАЕМ
  deleteComment: async (taskId, commentId) => {
    console.log('⚠️ Delete comment endpoint not available');
    throw new Error('Удаление комментариев временно недоступно');
  },

  // Добавить вложение (исправленная версия)
  // В разделе tasksAPI в api.js:
addAttachment: async (taskId, file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const config = {
      headers: {
        'X-API-Key': API_KEY,
      },
      onUploadProgress,
    };
    
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    console.log('📤 Uploading file config:', {
      taskId,
      filename: file.name,
      size: file.size,
      type: file.type,
    });
    
    try {
      const response = await axios.post(`${API_BASE_URL}/tasks/${taskId}/attachments`, formData, config);
      console.log('✅ File uploaded successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ File upload error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },

  // Добавить вложение
  // addAttachment: async (taskId, file) => {
  //   const formData = new FormData();
  //   formData.append('file', file);
    
  //   const response = await api.post(`/tasks/${taskId}/attachments`, formData, {
  //     headers: {
  //       'Content-Type': 'multipart/form-data',
  //     },
  //   });
  //   return response.data;
  // },

  // Скачать вложение
 downloadAttachment: async (taskId, attachmentId) => {
    const response = await api.get(`/tasks/${taskId}/attachments/${attachmentId}`, {
      responseType: 'blob'
    });
    return response.data;
  }
};

// Добавить в конец файла api.js (после tasksAPI)

export const chatAPI = {
  // Получить список комнат текущего пользователя
  getRooms: async () => {
    const response = await api.get('/chat/rooms');
    return response.data;
  },

  // Получить конкретную комнату
  getRoom: async (roomId) => {
    const response = await api.get(`/chat/rooms/${roomId}`);
    return response.data;
  },

  // Получить сообщения комнаты
  getMessages: async (roomId, params = {}) => {
    const response = await api.get(`/chat/rooms/${roomId}/messages`, { params });
    return response.data;
  },

  // Отправить сообщение (REST)
  sendMessage: async (roomId, data) => {
    const response = await api.post(`/chat/rooms/${roomId}/messages`, {
      room_id: roomId,          // ← добавляем room_id из параметра
      content: data.content,
      attachments: data.attachments || []
    });
    return response.data;
  },

  // Создать групповую комнату
  createGroupRoom: async (data) => {
    const response = await api.post('/chat/rooms', data);
    return response.data;
  },

  // Получить или создать личную комнату с пользователем
  getOrCreatePrivateRoom: async (otherUserId) => {
    const response = await api.post(`/chat/private/${otherUserId}`);
    return response.data;
  },

  // Добавить участника в группу
  addParticipant: async (roomId, data) => {
    const response = await api.post(`/chat/rooms/${roomId}/participants`, data);
    return response.data;
  },

  // Удалить участника из группы
  removeParticipant: async (roomId, userId) => {
    const response = await api.delete(`/chat/rooms/${roomId}/participants/${userId}`);
    return response.data;
  },

  // Покинуть группу
  leaveRoom: async (roomId) => {
    const response = await api.post(`/chat/rooms/${roomId}/leave`);
    return response.data;
  },

  markRoomAsRead: async (roomId) => {
    const response = await api.post(`/chat/rooms/${roomId}/read`);
    return response.data;
  },

   // Поиск по сообщениям
  searchMessages: async (roomId, query) => {
    const response = await api.get(`/chat/rooms/${roomId}/search`, {
      params: { q: query }
    });
    return response.data;
  },

  // Редактирование сообщения
  editMessage: async (roomId, messageId, content) => {
    const response = await api.put(`/chat/rooms/${roomId}/messages/${messageId}`, {
      content
    });
    return response.data;
  },

  // Удаление сообщения
  deleteMessage: async (roomId, messageId) => {
    const response = await api.delete(`/chat/rooms/${roomId}/messages/${messageId}`);
    return response.data;
  },

  // Получить информацию о пользователе для чата
  getUserChatInfo: async (userId) => {
    const response = await api.get(`/chat/users/${userId}/info`);
    return response.data;
  },

  // Отметить сообщения как прочитанные до определенного ID
  markMessagesAsRead: async (roomId, upToMessageId) => {
    const response = await api.post(`/chat/rooms/${roomId}/read`, {
      up_to_message_id: upToMessageId
    });
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await api.get('/chat/unread-count');
    return response.data;
  }
};



export default api;